// Maps Italian product color keywords → armocromia seasons that suit them.
// Order-independent; lookups sort keys by length so more specific matches
// ("blu notte") win over generic ones ("blu").
//
// The 4 main seasons:
//   - Primavera: warm + light + clear
//   - Estate:    cool + light + muted
//   - Autunno:   warm + deep + muted
//   - Inverno:   cool + deep + clear/saturated
const COLOR_TO_SEASONS = {
  // --- Inverno (cool, deep, high contrast / saturated) ---
  'nero': ['Inverno'],
  'bianco ottico': ['Inverno'],
  'antracite': ['Inverno'],
  'grigio antracite': ['Inverno'],
  'grigio scuro': ['Inverno'],
  'rosso cremisi': ['Inverno'],
  'rosso rubino': ['Inverno'],
  'rubino': ['Inverno'],
  'blu notte': ['Inverno'],
  'blu royal': ['Inverno'],
  'blu navy': ['Inverno'],
  'viola': ['Inverno'],
  'fucsia': ['Inverno'],
  'rosa shock': ['Inverno'],
  'magenta': ['Inverno'],
  'verde smeraldo': ['Inverno'],
  'smeraldo': ['Inverno'],
  'zaffiro': ['Inverno'],
  'teal': ['Inverno'],

  // --- Autunno (warm, deep, muted) ---
  'marrone': ['Autunno'],
  'marrone cioccolato': ['Autunno'],
  'cioccolato': ['Autunno'],
  'testa di moro': ['Autunno'],
  'moro': ['Autunno'],
  'ruggine': ['Autunno'],
  'senape': ['Autunno'],
  'giallo senape': ['Autunno'],
  'verde oliva': ['Autunno'],
  'oliva': ['Autunno'],
  'verde bosco': ['Autunno'],
  'verde foresta': ['Autunno'],
  'verde militare': ['Autunno'],
  'verde petrolio': ['Autunno'],
  'kaki': ['Autunno'],
  'cachi': ['Autunno'],
  'bordeaux': ['Autunno', 'Inverno'],
  'mattone': ['Autunno'],
  'rosso mattone': ['Autunno'],
  'terracotta': ['Autunno'],
  'ocra': ['Autunno'],
  'tabacco': ['Autunno'],
  'cognac': ['Autunno'],
  'cammello': ['Autunno', 'Primavera'],
  'beige caldo': ['Autunno', 'Primavera'],
  'arancio bruciato': ['Autunno'],
  'prugna': ['Autunno', 'Inverno'],

  // --- Primavera (warm, light, clear) ---
  'corallo': ['Primavera'],
  'pesca': ['Primavera'],
  'salmone': ['Primavera'],
  'rosa salmone': ['Primavera'],
  'verde acqua': ['Primavera', 'Estate'],
  'verde mela': ['Primavera'],
  'giallo sole': ['Primavera'],
  'giallo limone': ['Primavera'],
  'turchese': ['Primavera', 'Estate'],
  'turchese caldo': ['Primavera'],
  'rosa confetto': ['Primavera'],
  'bianco panna': ['Primavera', 'Autunno'],
  'panna': ['Primavera', 'Autunno'],
  'avorio': ['Primavera', 'Autunno'],
  'oro': ['Primavera', 'Autunno'],
  'dorato': ['Primavera', 'Autunno'],
  'sabbia': ['Primavera', 'Autunno'],

  // --- Estate (cool, light, muted) ---
  'rosa cipria': ['Estate'],
  'rosa polvere': ['Estate'],
  'rosa antico': ['Estate'],
  'lavanda': ['Estate'],
  'lilla': ['Estate'],
  'malva': ['Estate'],
  'blu polvere': ['Estate'],
  'blu acciaio': ['Estate'],
  'blu chiaro': ['Estate', 'Primavera'],
  'azzurro': ['Estate', 'Primavera'],
  'azzurro polvere': ['Estate'],
  'celeste': ['Estate', 'Primavera'],
  'verde salvia': ['Estate'],
  'salvia': ['Estate'],
  'verde menta': ['Estate', 'Primavera'],
  'menta': ['Estate', 'Primavera'],
  'grigio perla': ['Estate'],
  'tortora': ['Estate'],
  'bianco latte': ['Estate'],
  'prugna chiaro': ['Estate'],
  'argento': ['Estate', 'Inverno'],

  // --- Generic / ambiguous terms (lower signal — multi-season) ---
  'rosso': ['Inverno', 'Autunno'],
  'blu scuro': ['Inverno'],
  'blu': ['Inverno', 'Estate'],
  'rosa': ['Estate', 'Primavera'],
  'rosa chiaro': ['Estate', 'Primavera'],
  'rosa scuro': ['Inverno'],
  'giallo': ['Primavera', 'Autunno'],
  'arancione': ['Primavera', 'Autunno'],
  'arancio': ['Primavera', 'Autunno'],
  'verde chiaro': ['Primavera', 'Estate'],
  'verde scuro': ['Autunno', 'Inverno'],
  'verde': ['Primavera', 'Autunno', 'Estate'],
  'bianco': ['Primavera', 'Estate', 'Autunno', 'Inverno'],
  'grigio chiaro': ['Estate'],
  'grigio': ['Inverno', 'Estate'],
  'beige': ['Autunno', 'Primavera'],
  'marrone chiaro': ['Autunno', 'Primavera']
};

// Keys sorted once by length (longest first) so "blu notte" matches before "blu".
const COLOR_KEYS_BY_LENGTH = Object.keys(COLOR_TO_SEASONS).sort(
  (a, b) => b.length - a.length
);

function normalize(s) {
  return String(s || '').toLowerCase().trim();
}

// Find the most specific color-map entry contained in `productColor`.
// Returns { key, seasons } or null.
function findBestColorEntry(productColor) {
  const color = normalize(productColor);
  if (!color) return null;
  for (const key of COLOR_KEYS_BY_LENGTH) {
    if (color.includes(key)) return { key, seasons: COLOR_TO_SEASONS[key] };
  }
  return null;
}

// 0 if no data, 1.0–1.6 if the season is in the product's color map.
// More specific colors (fewer seasons) score higher.
export function seasonFitScore(productColor, targetSeason) {
  if (!targetSeason) return 0;
  const entry = findBestColorEntry(productColor);
  if (!entry) return 0;
  if (!entry.seasons.includes(targetSeason)) return 0;
  // Bonus for specificity: a color that only suits 1 season scores higher
  // than one that suits 4.
  return 1 + (4 - entry.seasons.length) * 0.15;
}

// Full product-vs-armocromia score. Used by loadRecommendations.
// Signals (highest priority first):
//   1. Exact match against AI-returned recommendedColors  → +6
//   2. Exact match against AI-returned colorsToAvoid     → -5
//   3. Season fit from static color→season map           → +0..+6.4
export function scoreAgainstArmocromia(productColor, armocromiaData) {
  if (!armocromiaData) return 0;
  const color = normalize(productColor);
  if (!color) return 0;

  const rec = (armocromiaData.recommendedColors || []).map(normalize).filter(Boolean);
  const avoid = (armocromiaData.colorsToAvoid || []).map(normalize).filter(Boolean);

  for (const rc of rec) {
    if (color.includes(rc) || rc.includes(color)) return 6;
  }
  for (const ac of avoid) {
    if (color.includes(ac) || ac.includes(color)) return -5;
  }

  return seasonFitScore(color, armocromiaData.season) * 4;
}

// Returns the list of seasons this product's color is ideal for (for badges).
export function productSeasonTags(productColor) {
  const entry = findBestColorEntry(productColor);
  return entry ? entry.seasons : [];
}
